import { getSalesContractDetailsByIdApi } from "../../../../services/_sales";

// Calculation Function
export const n = (v: any) => parseFloat(v || "0");
export const calculate = (val: any) => {
  const formatted = val && val > 0 ? +n(val).toFixed(3) : "";
  return formatted;
};

const totalOfAllProducts = (values: any, field: any) => {
  const totals = {
    quantity: 0,
    amount: 0,
    bags: 0,
    net: 0,
    gross: 0,
  };

  if (
    [
      "total_quantity",
      "total_amount",
      "total_no_of_bags",
      "total_net_weight",
      "total_gross_weight",
      "both",
    ].includes(field)
  ) {
    for (const p of values.sales_invoice_products || []) {
      totals.quantity += n(p.quantity);
      totals.amount += n(p.total_amount);
      totals.bags += n(p.no_of_bags);
      totals.net += n(p.total_net_weight);
      totals.gross += n(p.total_gross_weight);
    }
  }

  return totals;
};

export const handleProductAndContainerCalculations = (
  values: any,
  setFieldValue: Function,
  index: number,
) => {
  const product = values.sales_invoice_products[index];
  const n = (val: any) => Number(val) || 0;

  const price = n(product.price);
  const quantity = n(product.quantity);
  const no_of_bags = n(product.no_of_bags);
  const packing_net_weight = n(product.packing_net_weight);
  const packing_gross_weight = n(product.packing_gross_weight);
  const exchange_rate = n(product.exchange_rate);
  const igst_percentage = n(product.igst_percentage);
  const commission_rate = n(product.commission_rate);

  //  Product Level Calculations
  const total_amount = price * quantity;
  const total_net_weight = no_of_bags * packing_net_weight;
  const total_gross_weight = no_of_bags * packing_gross_weight;
  const total_taxable_amount_inr = total_amount * exchange_rate;
  const total_igst_amount_inr =
    (igst_percentage * total_taxable_amount_inr) / 100;
  const commission_amount = (commission_rate * total_amount) / 100;

  //  Update Product Fields
  setFieldValue(`sales_invoice_products[${index}].total_amount`, total_amount);
  setFieldValue(
    `sales_invoice_products[${index}].total_net_weight`,
    total_net_weight,
  );
  setFieldValue(
    `sales_invoice_products[${index}].total_gross_weight`,
    total_gross_weight,
  );
  setFieldValue(
    `sales_invoice_products[${index}].total_taxable_amount_inr`,
    total_taxable_amount_inr,
  );
  setFieldValue(
    `sales_invoice_products[${index}].total_igst_amount_inr`,
    total_igst_amount_inr,
  );
  setFieldValue(
    `sales_invoice_products[${index}].commission_amount`,
    commission_amount,
  );

  //  Update Container Fields
  if (Array.isArray(product.containers)) {
    product.containers.forEach((container: any, containerIndex: number) => {
      const containerBags = n(container.no_of_bags);
      const containerNet = containerBags * packing_net_weight;
      const containerGross = containerBags * packing_gross_weight;

      setFieldValue(
        `sales_invoice_products[${index}].containers[${containerIndex}].total_net_weight`,
        containerNet,
      );
      setFieldValue(
        `sales_invoice_products[${index}].containers[${containerIndex}].total_gross_weight`,
        containerGross,
      );
    });
  }
};

export const handleSalesInvoiceCalculation = (
  field: any,
  values: any,
  setFieldValue: any,
) => {
  const { amount, quantity, bags, net, gross } = totalOfAllProducts(
    values,
    field,
  );

  const updateValue = (key: string, val: any) =>
    setFieldValue(key, val && val > 0 ? +n(val).toFixed(3) : "");

  // both = total_pricing & total_packing details
  // 1. Total pricing details
  if (["total_amount", "total_quantity", "both"].includes(field)) {
    updateValue("total_amount", amount);
    updateValue("total_quantity", quantity);

    const taxable = amount * n(values.exchange_rate);
    const igst = (taxable * n(values.igst_percentage)) / 100;
    const commission = (amount * n(values.commission_rate)) / 100;

    updateValue("total_taxable_amount_inr", taxable);
    updateValue("total_igst_amount_inr", igst);
    updateValue("commission_amount", commission);
  }

  if (field === "total_taxable_amount") {
    const taxable = n(values.total_amount) * n(values.exchange_rate);
    const igst = (taxable * n(values.igst_percentage)) / 100;
    updateValue("total_taxable_amount_inr", taxable);
    updateValue("total_igst_amount_inr", igst);
  }

  if (field === "total_igst_amount") {
    const igst =
      (n(values.total_taxable_amount_inr) * n(values.igst_percentage)) / 100;
    updateValue("total_igst_amount_inr", igst);
  }

  if (field === "commission_amount") {
    const commission =
      (n(values.total_amount) * n(values.commission_rate)) / 100;
    updateValue("commission_amount", commission);
  }

  // 2. Total packing details
  if (field === "total_no_of_bags" || field === "both") {
    updateValue("total_no_of_bags", bags);
    updateValue("total_net_weight", net);
    updateValue("total_gross_weight", gross);
  }

  if (field === "total_net_weight") {
    updateValue("total_net_weight", net);
  }

  if (field === "total_gross_weight") {
    updateValue("total_gross_weight", gross);
  }
};

export const getSalesContractDetailsWithUsedQty = async (
  salesContractId: string,
  invoiceId?: string,
) => {
  const result: any = await getSalesContractDetailsByIdApi(salesContractId);
  if (!result?.isSuccess) return null;

  const productWiseUsedQty = (result.data.sales_invoice_products || []).reduce(
    (acc: { [key: string]: number }, product: any) => {
      if (!invoiceId || product.sales_invoice_id !== invoiceId) {
        const key = `${product.product_id}_${product.sales_contract_id}`;

        acc[key] = n(calculate((acc[key] || 0) + n(product.quantity)));
      }

      return acc;
    },
    {},
  );
  return { sales_contract: result.data, productWiseUsedQty };
};
// this function subtract product used quantity from sales_contract product total quantity
export const getSalesContractDetailsWithRemainingQty = async (
  salesContractId: string,
  invoiceId?: string,
) => {
  const res = await getSalesContractDetailsWithUsedQty(
    salesContractId,
    invoiceId,
  );

  if (!res) return null;

  const { sales_contract, productWiseUsedQty } = res;

  const groupedProducts = groupContractProductsByParent(sales_contract);

  const productWiseRemainingQty: any = {};

  groupedProducts.forEach((p: any) => {
    const key = `${p.product_id}_${salesContractId}`;

    const used = productWiseUsedQty[key] || 0;

    productWiseRemainingQty[key] = calculate(p.quantity - used);
  });
  return {
    sales_contract: {
      ...sales_contract,
      sales_contract_product: groupedProducts,
    },
    productWiseRemainingQty,
  };
};

const groupContractProductsByParent = (contract: any) => {
  const map: any = {};

  contract.sales_contract_product.forEach((p: any) => {
    const parentId = p.products?.parent_product_id || p.product_id;

    const key = `${parentId}_${contract.id}`;

    if (!map[key]) {
      map[key] = {
        product_id: parentId,
        sales_contract_id: contract.id,
        price: p.price,
        quantity: 0,
        no_of_bags: 0,
        no_of_containers: 0,
        packing_net_weight: p.packing_net_weight,
        packing_gross_weight: p.packing_gross_weight,
        container_load: p.container_load,
        packing_type_id: p.packing_type_id,
        products: {
          name: p.products?.name,
        },
      };
    }

    map[key].quantity += n(p.quantity);
    map[key].no_of_bags += n(p.no_of_bags);
    map[key].no_of_containers += n(p.no_of_containers);
  });

  return Object.values(map);
};

export const calculateInvoiceTotals = (values: any) => {
  let totalAmount = 0;
  let totalQuantity = 0;
  let totalNoOfBags = 0;
  let totalNetWeight = 0;
  let totalGrossWeight = 0;

  values.sales_invoice_products.forEach((product: any) => {
    totalAmount += parseFloat(product.total_amount || 0);
    totalQuantity += parseFloat(product.quantity || 0);
    totalNoOfBags += parseFloat(product.no_of_bags || 0);
    totalNetWeight += parseFloat(product.total_net_weight || 0);
    totalGrossWeight += parseFloat(product.total_gross_weight || 0);
  });

  values.total_amount = totalAmount.toFixed(2);
  values.total_quantity = totalQuantity;
  values.total_no_of_bags = totalNoOfBags;
  values.total_net_weight = totalNetWeight;
  values.total_gross_weight = totalGrossWeight;

  return values;
};
